import React, { useState } from 'react';
import { X, CreditCard, Lock, AlertCircle, DollarSign } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PromoCodeInput from './PromoCodeInput';

// Remplacez par votre clé publique Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const CheckoutForm = ({ amount, onSuccess, onCancel, bookingDetails, serviceCategory }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentOption, setPaymentOption] = useState('deposit'); // 'deposit' ou 'full'

  const [promoData, setPromoData] = useState(null);
  const [discountedAmount, setDiscountedAmount] = useState(amount);

  const baseAmount = promoData ? promoData.newAmount : amount;
  const depositAmount = Math.round(amount * 0.20 * 100) / 100; // 20% arrondi à 2 décimales
  const fullAmount = baseAmount;
  const selectedAmount = paymentOption === 'deposit' ? depositAmount : fullAmount;

  const handlePromoApplied = (promo) => {
    setPromoData(promo);
    setDiscountedAmount(promo.newAmount);
  };

  const handlePromoRemoved = () => {
    setPromoData(null);
    setDiscountedAmount(amount);
  };  

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Créer le Payment Intent côté serveur
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: selectedAmount,
          paymentType: paymentOption, // 'deposit' ou 'full'
          bookingDetails: {
            ...bookingDetails,
            promoCode: promoData?.code || null,
            promoDiscount: promoData?.discount || 0,
            originalAmount: amount,
            finalAmount: baseAmount
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la création du paiement');
      }

      const { clientSecret, paymentIntentId } = await response.json();

      if (!clientSecret) {
        throw new Error('Client secret manquant dans la réponse');
      }

      // Confirmer le paiement
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: bookingDetails.userName,
            email: bookingDetails.userEmail
          }
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
        // Paiement réussi - passer le type de paiement et infos promo
        onSuccess(paymentIntentId, paymentOption, selectedAmount, promoData);
      }
    } catch (err) {
      console.error('Erreur paiement complète:', err);
      setError(err.message || 'Une erreur est survenue lors du paiement. Veuillez réessayer.');
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        padding: '12px'
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <div className="space-y-6">
      {/* Composant de code promo */}
      <PromoCodeInput
        originalAmount={amount}
        onPromoApplied={handlePromoApplied}
        onPromoRemoved={handlePromoRemoved}
        serviceCategory={serviceCategory}
        serviceName={bookingDetails?.serviceName || ''}
      />

      {/* Choix du type de paiement */}
      <div>
        <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          Choisissez votre mode de paiement
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Option Accompte */}
          <button
            type="button"
            onClick={() => setPaymentOption('deposit')}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              paymentOption === 'deposit'
                ? 'border-purple-600 bg-purple-50'
                : 'border-gray-200 hover:border-purple-300'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  paymentOption === 'deposit' 
                    ? 'border-purple-600 bg-purple-600' 
                    : 'border-gray-300'
                }`}>
                  {paymentOption === 'deposit' && (
                    <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                  )}
                </div>
                <span className="font-semibold text-gray-900">Accompte</span>
              </div>
              <span className="text-2xl font-bold text-purple-700">{depositAmount}€</span>
            </div>
            <p className="text-sm text-gray-600 ml-7">
              Payez 20% maintenant, le reste sur place
            </p>
            <p className="text-xs text-purple-600 ml-7 mt-1">
              Restant à régler : {(amount - depositAmount).toFixed(2)}€
            </p>
          </button>

          {/* Option Paiement complet */}
          <button
            type="button"
            onClick={() => setPaymentOption('full')}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              paymentOption === 'full'
                ? 'border-green-600 bg-green-50'
                : 'border-gray-200 hover:border-green-300'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  paymentOption === 'full' 
                    ? 'border-green-600 bg-green-600' 
                    : 'border-gray-300'
                }`}>
                  {paymentOption === 'full' && (
                    <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                  )}
                </div>
                <span className="font-semibold text-gray-900">Paiement complet</span>
              </div>
              <span className="text-2xl font-bold text-green-700">{fullAmount}€</span>
            </div>
            <p className="text-sm text-gray-600 ml-7">
              Payez la totalité maintenant
            </p>
            <p className="text-xs text-green-600 ml-7 mt-1">
              ✓ Réservation totalement réglée
            </p>
          </button>
        </div>
      </div>

      {/* Récapitulatif */}
      <div className={`rounded-lg p-4 ${
        paymentOption === 'deposit' ? 'bg-purple-50' : 'bg-green-50'
      }`}>
        <h4 className="font-semibold text-gray-900 mb-3">Récapitulatif de paiement</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-700">Montant total du soin :</span>
            <span className="font-semibold text-gray-900">{amount}€</span>
          </div>

          {/* Affichage réduction code promo si appliquée */}
            {promoData && (
              <>
                <div className="flex justify-between text-green-700">
                  <span className="flex items-center gap-1">
                    Réduction ({promoData.code})
                  </span>
                  <span className="font-semibold">-{promoData.discount}€</span>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-2">
                  <span className="text-gray-700 font-medium">Nouveau total :</span>
                  <span className="font-bold text-gray-900">{baseAmount}€</span>
                </div>
              </>
            )}       

          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="text-gray-700 font-medium">
              {paymentOption === 'deposit' ? 'Accompte à payer (20%)' : 'Montant à payer'}
            </span>
            <span className={`font-bold text-lg ${
              paymentOption === 'deposit' ? 'text-purple-700' : 'text-green-700'
            }`}>
              {selectedAmount}€
            </span>
          </div>
          {paymentOption === 'deposit' && (
            <div className="flex justify-between text-xs text-gray-500 pt-1">
              <span>Restant à régler sur place :</span>
              <span>{(amount - depositAmount).toFixed(2)}€</span>
            </div>
          )}
          {paymentOption === 'full' && (
            <div className="flex items-center gap-2 text-xs text-green-700 pt-1">
              <span>✓ Prestation entièrement réglée</span>
            </div>
          )}
        </div>
      </div>

      {/* Informations importantes */}
      <div className="bg-amber-50 border-l-4 border-amber-500 p-4">
        <div className="flex gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">Politique d'annulation :</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Annulation gratuite jusqu'à 48h avant le rendez-vous</li>
              <li>
                {paymentOption === 'deposit' 
                  ? "L'accompte sera remboursé intégralement en cas d'annulation"
                  : "Le montant total sera remboursé intégralement en cas d'annulation"
                }
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Formulaire de carte */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <CreditCard className="w-4 h-4 inline mr-2" />
          Informations de paiement
        </label>
        <div className="border border-gray-300 rounded-lg p-4 bg-white">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Badge sécurité */}
      <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
        <Lock className="w-4 h-4" />
        <span>Paiement sécurisé par Stripe</span>
      </div>

      {/* Boutons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={loading}
        >
          Annuler
        </button>
        <button
          onClick={handleSubmit}
          disabled={!stripe || loading}
          className={`flex-1 ${
            paymentOption === 'deposit' 
              ? 'bg-purple-600 hover:bg-purple-700' 
              : 'bg-green-600 hover:bg-green-700'
          } text-white px-6 py-3 rounded-lg font-medium transition-colors ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {loading ? 'Traitement...' : `Payer ${selectedAmount}€`}
        </button>
      </div>
    </div>
  );
};

const PaymentModal = ({ isOpen, onClose, amount, onPaymentSuccess, bookingDetails, serviceCategory }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-light text-purple-900 mb-2">
              Finaliser votre réservation
            </h2>
            <p className="text-gray-600">Paiement sécurisé</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <Elements stripe={stripePromise}>
          <CheckoutForm
            amount={amount}
            onSuccess={onPaymentSuccess}
            onCancel={onClose}
            bookingDetails={bookingDetails}
            serviceCategory={serviceCategory}
          />
        </Elements>
      </div>
    </div>
  );
};

export default PaymentModal;